import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { quickApi, entitiesApi, projectsApi, contactsApi } from "@/lib/api";
import ContactPicker from "@/components/ContactPicker";
import MultiContactPicker from "@/components/MultiContactPicker";
import CurrencyInput from "@/components/CurrencyInput";
import CurrencySelect from "@/components/CurrencySelect";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Trash2, Wand2, Settings2, X, Inbox as InboxIcon } from "lucide-react";
import { intakeApi, filesApi } from "@/lib/api";
import { showIntakeConfirmToast } from "@/lib/intakeToast";
import { CaptureForm as IntakeCaptureForm, ReviewPane as IntakeReviewPane } from "@/pages/IntakePage";

// -------- Types + section metadata --------
const TYPE_LABELS = {
  entity: "Entity",
  project: "Project",
  project_from_template: "From template",
  task: "Task",
  calendar: "Event",
  expenditure: "Budget entry",
  note: "Note",
  contact: "Contact",
  email: "Email",
};
const TYPES = Object.keys(TYPE_LABELS);

const SECTION_EXAMPLES = {
  entity: [
    "Create Alias Distribution as an active entity for company-wide operations.",
    "Add B. Ruff. Ton. Productions as an active production company entity.",
    "Set up a new entity called Ruta with COP as its currency and a $10,000 approved budget.",
  ],
  projects: [
    "Create a production project for the live session with a $7,500 budget.",
    "Add an initiative to redesign the investor deck, target completion end of month, high priority.",
    "New project: Q2 marketing push under Alias Compass, Active, medium priority.",
  ],
  tasks: [
    "Send payment to EthioJobs by Friday. High priority. Assign to me.",
    "Draft investor deck v3 by tomorrow and schedule review with Sarah next Monday.",
    "Follow up with printer about March shoot proofs — urgent.",
  ],
  calendar: [
    "Schedule a production meeting with Sarah next Tuesday at 2 PM.",
    "Add a deadline for the investor deck on Friday.",
    "Production shoot on March 14 all day at Studio B.",
  ],
  budget: [
    "Record a $450 camera rental expense.",
    "Received $12,000 revenue from March shoot invoice today.",
    "$180 outstanding for last week's catering.",
  ],
  notes: [
    "Note that we decided to film outdoors at sunrise.",
    "Idea: launch a members-only preview two weeks before public release.",
    "Meeting notes from investor sync: Sarah wants revised traction slide by Friday.",
  ],
  contacts: [
    "Add Marcus Lee as the cinematographer, marcus@studio.com.",
    "New contact: Sarah Kim, VP at Mint Capital, sarah@mint.co, phone 415-555-0102.",
    "Add Jamie Rivera as a producer for Production Company.",
  ],
  email: [
    "Draft an email to Sarah confirming the production meeting.",
    "Write to Marcus to follow up on the March shoot budget.",
    "Email Jamie a thank-you note for the recent review call.",
  ],
  global: [
    "Create a production project for the live session with a $7,500 budget.",
    "Schedule a production meeting with Sarah next Tuesday at 2 PM.",
    "Record a $450 camera rental expense.",
    "Note that we decided to film outdoors at sunrise.",
  ],
};

const SECTION_TITLES = {
  entity: "Quick Add · Entity",
  projects: "Quick Add · Projects",
  tasks: "Quick Add · Tasks",
  calendar: "Quick Add · Calendar",
  budget: "Quick Add · Budget",
  notes: "Quick Add · Notes",
  contacts: "Quick Add · Contacts",
  email: "Quick Add · Email",
  global: "Quick Add",
};

const SECTION_PLACEHOLDER = {
  entity: 'e.g. "Create Alias Distribution as an active entity for company-wide operations."',
  projects: 'e.g. "Create a production project for the live session with a $7,500 budget."',
  tasks: 'e.g. "Send payment to EthioJobs by Friday. High priority. Assign to me."',
  calendar: 'e.g. "Schedule a production meeting with Sarah next Tuesday at 2 PM."',
  budget: 'e.g. "Record a $450 camera rental expense."',
  notes: 'e.g. "Note that we decided to film outdoors at sunrise."',
  contacts: 'e.g. "Add Marcus as the cinematographer, marcus@studio.com."',
  email: 'e.g. "Draft an email to Sarah confirming the production meeting."',
  global: 'Describe anything — an entity, task, event, note, expense, contact, project, or email.',
};

// -------- Component --------
export default function UniversalQuickAdd({
  open, setOpen, section = "global",
  defaultEntityId, defaultProjectId, defaultContactId,
  prefillPrompt,
  onCreated, onEditDetails,
}) {
  const [mode, setMode] = useState("quick"); // "quick" | "intake"
  const [step, setStep] = useState("input"); // input | preview
  const [prompt, setPrompt] = useState("");
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);

  // Intake-mode state — deliberately isolated from the quick-parse state
  // so switching tabs doesn't drop half-typed content.
  const [inSourceType, setInSourceType] = useState("text");
  const [inText, setInText] = useState("");
  const [inUrl, setInUrl] = useState("");
  const [inImageFile, setInImageFile] = useState(null);
  const [inImageFileId, setInImageFileId] = useState(null);
  const [inImagePreview, setInImagePreview] = useState(null);
  const [inUploading, setInUploading] = useState(false);
  const [inAnalyzing, setInAnalyzing] = useState(false);
  const [inItem, setInItem] = useState(null);           // IntakeItem being reviewed
  const [inFlags, setInFlags] = useState({});
  const [inEdits, setInEdits] = useState({});
  const [inConfirming, setInConfirming] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("quick");
      setStep("input"); setPrompt(prefillPrompt || ""); setRecords([]);
      // Reset intake state on every open — a stale IntakeItem in the modal
      // would confuse a follow-up capture.
      setInSourceType("text"); setInText(""); setInUrl("");
      setInImageFile(null); setInImageFileId(null); setInImagePreview(null);
      setInItem(null); setInFlags({}); setInEdits({});
      Promise.all([entitiesApi.list(), projectsApi.list()]).then(([e, p]) => {
        setEntities(e.data); setProjects(p.data);
      }).catch(() => {});
    }
  }, [open, prefillPrompt]);

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);

  const examples = SECTION_EXAMPLES[section] || SECTION_EXAMPLES.global;

  const parse = async () => {
    if (!prompt.trim()) return toast.error("Please describe what to add");
    setParsing(true);
    try {
      const r = await quickApi.parse({
        prompt: prompt.trim(),
        timezone: tz,
        section_hint: section === "global" ? null : section,
        default_entity_id: defaultEntityId || null,
        default_project_id: defaultProjectId || null,
        default_contact_id: defaultContactId || null,
      });
      const recs = r.data.records || [];
      if (!recs.length) return toast.error("Couldn't detect a record. Try rephrasing.");
      setRecords(recs);
      setStep("preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parsing failed");
    } finally {
      setParsing(false);
    }
  };

  const updateRecord = (idx, patch) =>
    setRecords((rs) => rs.map((r, i) => (i === idx ? { ...r, fields: { ...r.fields, ...patch } } : r)));
  const changeType = (idx, newType) =>
    setRecords((rs) => rs.map((r, i) => (i === idx ? { ...r, type: newType } : r)));
  const removeRecord = (idx) =>
    setRecords((rs) => rs.filter((_, i) => i !== idx));

  const commit = async () => {
    if (!records.length) return;
    // Basic validation per record
    for (let i = 0; i < records.length; i++) {
      const { type, fields } = records[i];
      if (type !== "contact" && type !== "entity" && !fields.entity_id) return toast.error(`Record ${i + 1}: choose an entity`);
      if (type === "entity" && !fields.name?.trim()) return toast.error(`Record ${i + 1}: entity name required`);
      if (type === "entity" && fields.duplicate_of_id && !fields.allow_duplicate) return toast.error(`Record ${i + 1}: an entity named "${fields.duplicate_of_name}" already exists. Confirm to create a duplicate.`);
      if (type === "project" && !fields.name?.trim()) return toast.error(`Record ${i + 1}: project name required`);
      if (type === "project_from_template") {
        if (!fields.entity_id) return toast.error(`Record ${i + 1}: choose an entity`);
        if (!fields.template_id) return toast.error(`Record ${i + 1}: matched template not found — pick another prompt`);
        if (!fields.start_date) return toast.error(`Record ${i + 1}: start date required`);
      }
      if (type === "task" && !fields.title?.trim()) return toast.error(`Record ${i + 1}: task title required`);
      if (type === "calendar" && (!fields.title?.trim() || !fields.starts_at)) return toast.error(`Record ${i + 1}: event title and start time required`);
      if (type === "expenditure" && (!fields.amount || Number(fields.amount) <= 0)) return toast.error(`Record ${i + 1}: amount required`);
      if (type === "note" && (!fields.title?.trim() || !fields.content?.trim())) return toast.error(`Record ${i + 1}: note title and content required`);
      if (type === "contact" && !fields.full_name?.trim()) return toast.error(`Record ${i + 1}: contact name required`);
      if (type === "email" && (!fields.contact_id || !fields.subject?.trim() || !fields.body?.trim())) return toast.error(`Record ${i + 1}: email needs a contact, subject, and body`);
    }
    setSaving(true);
    try {
      const r = await quickApi.commit(records);
      toast.success(`Created ${r.data.count} record${r.data.count === 1 ? "" : "s"}`);
      setOpen(false);
      onCreated?.(r.data.created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const openDetails = (rec) => {
    // Hand off the draft to the full detailed dialog. Parent decides how to open it.
    setOpen(false);
    onEditDetails?.(rec);
  };

  // ------- Intake-mode handlers (mirror IntakePage exactly; the same
  // backend endpoints do the work so there is only one Intake pipeline). -------
  const inUploadImage = async (f) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) return toast.error("Only PNG / JPEG / WEBP images accepted");
    if (f.size > 12 * 1024 * 1024) return toast.error("Image is over 12 MB");
    setInUploading(true);
    try {
      const r = await filesApi.upload(f);
      setInImageFile(f); setInImageFileId(r.data.id);
      const reader = new FileReader();
      reader.onload = (ev) => setInImagePreview(ev.target.result);
      reader.readAsDataURL(f);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setInUploading(false); }
  };

  const inAnalyze = async () => {
    if (inSourceType === "text" && !inText.trim()) return toast.error("Paste some text first");
    if (inSourceType === "url" && !inUrl.trim()) return toast.error("Enter a URL");
    if (inSourceType === "image" && !inImageFileId) return toast.error("Upload an image first");
    setInAnalyzing(true);
    try {
      const r = await intakeApi.analyze({
        source_type: inSourceType,
        text: inText || null,
        url: inSourceType === "url" ? inUrl : null,
        file_id: inSourceType === "image" ? inImageFileId : null,
      });
      // Blocked URL fetch — Universal Quick Add doesn't host the full
      // recovery affordance (that lives on the /intake page). Send the
      // user there without corrupting Quick Add state.
      if (r.data?.status === "fetch_blocked") {
        toast.info("This page couldn't be accessed directly.", {
          description: "Open Intake to retry via screenshot or pasted content.",
          duration: 7000,
        });
        setInAnalyzing(false);
        return;
      }
      setInItem(r.data);
      const flags = {};
      (r.data.suggestions || []).forEach(s => { flags[s.id] = true; });
      setInFlags(flags); setInEdits({});
      setStep("preview");
      if ((r.data.suggestions || []).length === 0) {
        toast.info("No routing matches found. Source captured — review it in the Intake page.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setInAnalyzing(false); }
  };

  const inSuggValue = (sug, key) => {
    const eObj = inEdits[sug.id] || {};
    return eObj[key] !== undefined ? eObj[key] : sug[key];
  };
  const inUpdateSugg = (sug, patch) =>
    setInEdits(prev => ({ ...prev, [sug.id]: { ...(prev[sug.id] || {}), ...patch } }));

  const inConfirm = async () => {
    if (!inItem) return;
    const chosen = (inItem.suggestions || [])
      .filter(s => inFlags[s.id])
      .map(s => ({
        entity_id: inSuggValue(s, "entity_id"),
        project_id: inSuggValue(s, "project_id"),
        record_type: inSuggValue(s, "record_type"),
        title: inSuggValue(s, "title"),
        content: inSuggValue(s, "content"),
        contact: inSuggValue(s, "contact") || undefined,
        organization: inSuggValue(s, "organization") || undefined,
        why: inSuggValue(s, "why"),
        matched_signals: inSuggValue(s, "matched_signals"),
        confidence: inSuggValue(s, "confidence"),
      }));
    if (!chosen.length) return toast.error("Nothing selected — check at least one suggestion");
    setInConfirming(true);
    try {
      const r = await intakeApi.confirm(inItem.id, chosen);
      showIntakeConfirmToast(r.data.created);
      setOpen(false);
      onCreated?.(r.data.created);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setInConfirming(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white w-[95vw] sm:w-auto max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-white/70"/> {SECTION_TITLES[section] || "Quick Add"}
          </DialogTitle>
        </DialogHeader>

        {/* Mode toggle — only on the global (universal) tab. Section-scoped
            openings stay parse-only, because that's the surface people
            reach from an entity/project page and Intake would be off-topic. */}
        {section === "global" && (
          <div className="flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/[0.03] w-fit" data-testid="quick-add-mode-tabs">
            <button type="button" onClick={() => { setMode("quick"); setStep("input"); }}
              className={`px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${mode === "quick" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
              data-testid="quick-add-mode-quick">
              <Wand2 className="w-3.5 h-3.5"/> Quick parse
            </button>
            <button type="button" onClick={() => { setMode("intake"); setStep("input"); }}
              className={`px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${mode === "intake" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
              data-testid="quick-add-mode-intake">
              <InboxIcon className="w-3.5 h-3.5"/> Intake
            </button>
          </div>
        )}

        {mode === "intake" ? (
          <IntakeMode
            step={step} setStep={setStep}
            sourceType={inSourceType} setSourceType={setInSourceType}
            text={inText} setText={setInText}
            url={inUrl} setUrl={setInUrl}
            imageFile={inImageFile} imagePreview={inImagePreview}
            uploading={inUploading} onUpload={inUploadImage}
            analyzing={inAnalyzing} onAnalyze={inAnalyze}
            item={inItem} entities={entities} projects={projects}
            flags={inFlags} setFlags={setInFlags}
            suggValue={inSuggValue} updateSugg={inUpdateSugg}
            confirming={inConfirming} onConfirm={inConfirm}
            onCancel={() => setOpen(false)}
          />
        ) : (
          <>
        {step === "input" && (
          <div className="py-2 space-y-4" data-testid="quick-add-input-step">
            <p className="text-sm text-white/60 leading-relaxed">
              Describe what to add in plain language. The Executive Assistant will detect the type and prefill the fields — you'll get a chance to review before saving.
            </p>
            <Textarea
              autoFocus rows={5} value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={SECTION_PLACEHOLDER[section]}
              className="bg-white/5 border-white/10 text-sm"
              data-testid="quick-add-prompt"
            />
            <div>
              <div className="text-eyebrow mb-2">Examples</div>
              <div className="space-y-1.5">
                {examples.map((ex) => (
                  <button key={ex} type="button" onClick={() => setPrompt(ex)}
                    className="w-full text-left px-3 py-2 rounded-md bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 text-xs text-white/70"
                    data-testid="quick-add-example">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-[11px] text-white/40">Timezone: {tz}. Relative dates supported (today, tomorrow, Friday, next week, end of month).</div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto order-1 sm:order-1">Cancel</Button>
              <Button onClick={parse} disabled={parsing || !prompt.trim()} data-testid="quick-add-parse-btn"
                className="rounded-full bg-white text-black hover:bg-white/90 w-full sm:w-auto order-2 sm:order-2">
                <Sparkles className="w-4 h-4 mr-2"/> {parsing ? "Parsing…" : "Extract & preview"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "preview" && (
          <div className="py-2 space-y-4" data-testid="quick-add-preview-step">
            <div className="flex items-center justify-between">
              <button onClick={() => setStep("input")} className="text-xs text-white/60 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5"/> Edit prompt</button>
              <div className="text-xs text-white/50">{records.length} record{records.length === 1 ? "" : "s"} ready</div>
            </div>

            <div className="space-y-3">
              {records.map((rec, idx) => (
                <RecordCard
                  key={idx} idx={idx} rec={rec}
                  entities={entities} projects={projects}
                  onChangeType={(t) => changeType(idx, t)}
                  onUpdate={(patch) => updateRecord(idx, patch)}
                  onRemove={() => removeRecord(idx)}
                  onEditDetails={() => openDetails(rec)}
                  showRemove={records.length > 1}
                />
              ))}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto order-1 sm:order-1">Cancel</Button>
              <Button onClick={commit} disabled={saving || !records.length} data-testid="quick-add-create-btn"
                className="rounded-full bg-white text-black hover:bg-white/90 w-full sm:w-auto order-2 sm:order-2">
                {saving ? "Saving…" : `Save ${records.length} record${records.length === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </div>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------- Intake mode inside Universal Quick Add --------
// Uses the same CaptureForm + ReviewPane components as /intake, so a bug
// fix in one place fixes both surfaces. Calls the exact same
// /api/intake/* endpoints — there is only ever one Intake pipeline.
function IntakeMode({ step, setStep, sourceType, setSourceType, text, setText,
                     url, setUrl, imageFile, imagePreview, uploading, onUpload,
                     analyzing, onAnalyze, item, entities, projects,
                     flags, setFlags, suggValue, updateSugg,
                     confirming, onConfirm, onCancel }) {
  return (
    <div className="py-2 space-y-4" data-testid="quick-add-intake-step">
      {step === "input" ? (
        <>
          <p className="text-[11px] text-white/50">
            Drop text, a URL, or a screenshot. HQ analyses the source and proposes destinations across your entities before anything is saved. Nothing writes until you confirm.
          </p>
          <IntakeCaptureForm
            sourceType={sourceType} setSourceType={setSourceType}
            textVal={text} setTextVal={setText}
            urlVal={url} setUrlVal={setUrl}
            imageFile={imageFile} imagePreview={imagePreview}
            uploading={uploading} onUpload={onUpload}
            analyzing={analyzing} onAnalyze={onAnalyze}
            compact
          />
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="ghost" onClick={onCancel} className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto order-1 sm:order-1">Cancel</Button>
          </DialogFooter>
        </>
      ) : item ? (
        <>
          <div className="flex items-center justify-between">
            <button onClick={() => setStep("input")} className="text-xs text-white/60 hover:text-white flex items-center gap-1" data-testid="quick-add-intake-back">
              <ArrowLeft className="w-3.5 h-3.5"/> Edit source
            </button>
            <div className="text-xs text-white/50">
              {(item.suggestions || []).length} suggestion{(item.suggestions || []).length === 1 ? "" : "s"}
            </div>
          </div>
          <IntakeReviewPane
            item={item} entities={entities} projects={projects}
            confirmedFlags={flags} setConfirmedFlags={setFlags}
            suggValue={suggValue} updateSugg={updateSugg}
            confirming={confirming} onConfirm={onConfirm}
            onArchive={() => {}}
            compact hideArchive
          />
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="ghost" onClick={onCancel} className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto order-1 sm:order-1">Cancel</Button>
          </DialogFooter>
        </>
      ) : null}
    </div>
  );
}


// -------- Record card (per-type compact preview) --------
function RecordCard({ idx, rec, entities, projects, onChangeType, onUpdate, onRemove, onEditDetails, showRemove }) {
  const { type, fields, confidence } = rec;
  const projectsForEntity = projects.filter(p => p.entity_id === fields.entity_id);

  return (
    <div className="p-4 rounded-xl border border-white/10 bg-[#0F131B]" data-testid={`draft-${idx}`}>
      {/* Header: type selector + remove */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="text-eyebrow whitespace-nowrap">Detected as</div>
          <Select value={type} onValueChange={onChangeType}>
            <SelectTrigger className="h-8 bg-white/5 border-white/10 text-sm w-[130px] sm:w-[160px] flex-shrink-0" data-testid={`draft-type-${idx}`}>
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-[#151A23] border-white/10 text-white">
              {TYPES.map(t => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
          {typeof confidence === "number" && (
            <span className="text-[10px] text-white/40 whitespace-nowrap">{Math.round(confidence * 100)}%</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onEditDetails} className="px-2 py-1 rounded hover:bg-white/10 text-white/60 text-xs flex items-center gap-1 whitespace-nowrap" data-testid={`draft-edit-details-${idx}`}>
            <Settings2 className="w-3.5 h-3.5"/>
            <span className="hidden sm:inline">Edit details</span>
          </button>
          {showRemove && (
            <button onClick={onRemove} className="p-1.5 rounded hover:bg-red-500/10 text-red-400" title="Remove" data-testid={`draft-remove-${idx}`}><Trash2 className="w-3.5 h-3.5"/></button>
          )}
        </div>
      </div>

      {/* Entity + Project (common to most types). Contacts and top-level Entity
          creation don't need either. Projects belong to an Entity but never to
          another Project — hide the Project selector for the project type. */}
      {type !== "contact" && type !== "entity" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <EntitySelect entities={entities} value={fields.entity_id} onChange={(v) => onUpdate({ entity_id: v, project_id: null })} idx={idx}/>
          {type !== "project" && (
            <ProjectSelect projects={projectsForEntity} value={fields.project_id} onChange={(v) => onUpdate({ project_id: v })}/>
          )}
        </div>
      )}

      {/* Type-specific fields */}
      {type === "entity" && <EntityFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "project" && <ProjectFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "project_from_template" && <TemplateFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "task" && <TaskFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "calendar" && <CalendarFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "expenditure" && <ExpenditureFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "note" && <NoteFields fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "contact" && <ContactFields entities={entities} fields={fields} onUpdate={onUpdate} idx={idx}/>}
      {type === "email" && <EmailFields fields={fields} onUpdate={onUpdate} idx={idx}/>}

      {fields.entity_name && !fields.entity_id && type !== "contact" && (
        <div className="mt-2 text-[11px] text-amber-300">Couldn't match entity "{fields.entity_name}" — pick one above.</div>
      )}
    </div>
  );
}

const R = { label: "text-white/50 text-[10px] uppercase tracking-wider", input: "mt-1 bg-white/5 border-white/10 h-9 text-sm", trigger: "mt-1 bg-white/5 border-white/10 h-9 text-sm", content: "bg-[#151A23] border-white/10 text-white" };

function EntitySelect({ entities, value, onChange, idx }) {
  return (
    <div>
      <Label className={R.label}>Entity</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className={R.trigger} data-testid={`draft-entity-${idx}`}><SelectValue placeholder="Choose entity"/></SelectTrigger>
        <SelectContent className={R.content}>{entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function ProjectSelect({ projects, value, onChange }) {
  return (
    <div>
      <Label className={R.label}>Project</Label>
      <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? null : v)}>
        <SelectTrigger className={R.trigger}><SelectValue placeholder="None"/></SelectTrigger>
        <SelectContent className={R.content}>
          <SelectItem value="__none">None</SelectItem>
          {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---- Entity ----
function EntityFields({ fields, onUpdate, idx }) {
  const isDupe = !!fields.duplicate_of_id;
  return (
    <>
      <Input value={fields.name || ""} onChange={(e) => onUpdate({ name: e.target.value, duplicate_of_id: null, duplicate_of_name: null, allow_duplicate: false })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Entity name" data-testid={`draft-title-${idx}`}/>
      <Textarea rows={3} value={fields.description || ""} onChange={(e) => onUpdate({ description: e.target.value })}
        placeholder="Description / purpose (optional)" className="bg-white/5 border-white/10 text-sm mb-3"/>
      <Textarea rows={2} value={fields.objective || ""} onChange={(e) => onUpdate({ objective: e.target.value })}
        placeholder="Main objective (optional)" className="bg-white/5 border-white/10 text-sm mb-3"/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Status</Label>
          <Select value={fields.status || "Active"} onValueChange={(v) => onUpdate({ status: v })}>
            <SelectTrigger className={R.trigger} data-testid={`draft-entity-status-${idx}`}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["Active","Planning","On Hold","Archived"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className={R.label}>Approved budget</Label>
          <div className="mt-1 flex items-stretch gap-2">
            <div className="flex-1 min-w-0">
              <CurrencyInput value={fields.approved_budget} currency={fields.currency || "USD"} onChange={(v) => onUpdate({ approved_budget: Number(v) || 0 })} className="bg-white/5 border-white/10 h-9 text-sm"/>
            </div>
            <div className="w-24 flex-shrink-0">
              <CurrencySelect value={fields.currency || "USD"} onChange={(v) => onUpdate({ currency: v })}/>
            </div>
          </div>
        </div>
      </div>
      {isDupe && (
        <div className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-xs" data-testid={`draft-entity-duplicate-${idx}`}>
          <div className="text-amber-200 font-medium mb-1">An active entity named "{fields.duplicate_of_name}" already exists.</div>
          <div className="text-amber-100/70 mb-2">Rename this one, or check the box to create a duplicate anyway.</div>
          <label className="flex items-center gap-2 text-amber-100/90 cursor-pointer">
            <input type="checkbox" checked={!!fields.allow_duplicate} onChange={(e) => onUpdate({ allow_duplicate: e.target.checked })} data-testid={`draft-entity-allow-dupe-${idx}`}/>
            <span>Create anyway as a separate entity</span>
          </label>
        </div>
      )}
    </>
  );
}

// ---- Project ----
function ProjectFields({ fields, onUpdate, idx }) {
  return (
    <>
      <Input value={fields.name || ""} onChange={(e) => onUpdate({ name: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Project name" data-testid={`draft-title-${idx}`}/>
      <Textarea rows={4} value={fields.description || ""} onChange={(e) => onUpdate({ description: e.target.value })}
        placeholder="Description" className="bg-white/5 border-white/10 text-sm mb-3"/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Status</Label>
          <Select value={fields.status || "Planning"} onValueChange={(v) => onUpdate({ status: v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["Planning","Active","On Hold","Completed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className={R.label}>Priority</Label>
          <Select value={fields.priority || "Medium"} onValueChange={(v) => onUpdate({ priority: v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["Low","Medium","High","Critical"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className={R.label}>Phase</Label>
          <Input value={fields.phase || ""} onChange={(e) => onUpdate({ phase: e.target.value })} className={R.input}/></div>
        <div>
          <Label className={R.label}>Budget</Label>
          <div className="mt-1 flex items-stretch gap-2">
            <div className="flex-1 min-w-0">
              <CurrencyInput value={fields.budget} currency={fields.currency || "USD"} onChange={(v) => onUpdate({ budget: Number(v) || 0 })} className="bg-white/5 border-white/10 h-9 text-sm"/>
            </div>
            <div className="w-24 flex-shrink-0">
              <CurrencySelect value={fields.currency || "USD"} onChange={(v) => onUpdate({ currency: v })}/>
            </div>
          </div>
        </div>
        <div><Label className={R.label}>Start date</Label>
          <Input type="date" value={fields.start_date || ""} onChange={(e) => onUpdate({ start_date: e.target.value || null })} className={R.input}/></div>
        <div><Label className={R.label}>Target completion</Label>
          <Input type="date" value={fields.target_completion || ""} onChange={(e) => onUpdate({ target_completion: e.target.value || null })} className={R.input}/></div>
        <div className="sm:col-span-2">
          <Label className={R.label}>Owner</Label>
          <div className="mt-1" data-testid={`draft-owner-${idx}`}>
            {fields.owner_new_name && !fields.owner_contact_id ? (
              <div className="p-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] space-y-2">
                <div className="text-xs text-amber-200">New contact suggested — save will create it</div>
                <Input value={fields.owner_new_name} onChange={(e) => onUpdate({ owner_new_name: e.target.value })} placeholder="Name" className="bg-white/5 border-white/10 h-9 text-sm"/>
                <Input value={fields.owner_new_email || ""} onChange={(e) => onUpdate({ owner_new_email: e.target.value })} placeholder="Email (optional)" className="bg-white/5 border-white/10 h-9 text-sm"/>
                <div className="flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => onUpdate({ owner_new_name: null, owner_new_email: null })} className="text-white/60 hover:text-white hover:bg-white/10">Skip</Button>
                </div>
              </div>
            ) : (
              <ContactPicker
                value={fields.owner_contact_id || ""}
                onChange={(id) => onUpdate({ owner_contact_id: id || null })}
                entityId={fields.entity_id}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ---- Project from template ----
function TemplateFields({ fields, onUpdate, idx }) {
  return (
    <div className="space-y-3">
      <div className="text-xs">
        <span className="text-white/40 uppercase tracking-wider">Template:</span>{" "}
        <span className={fields.template_id ? "text-white/85 font-medium" : "text-amber-300"}>
          {fields.template_name || "(not detected)"}
        </span>
        {!fields.template_id && (
          <div className="mt-1 text-[11px] text-amber-300/80">
            Couldn't match a template. Create it from the Templates page first, or rephrase.
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Project name</Label>
          <Input value={fields.name || ""} onChange={(e) => onUpdate({ name: e.target.value })} className={R.input} data-testid={`draft-template-name-${idx}`}/>
        </div>
        <div>
          <Label className={R.label}>Start date</Label>
          <Input type="date" value={fields.start_date || ""} onChange={(e) => onUpdate({ start_date: e.target.value || null })} className={R.input} data-testid={`draft-template-start-${idx}`}/>
        </div>
        <div>
          <Label className={R.label}>Target completion (optional)</Label>
          <Input type="date" value={fields.target_completion || ""} onChange={(e) => onUpdate({ target_completion: e.target.value || null })} className={R.input}/>
        </div>
        <div>
          <Label className={R.label}>Priority (optional override)</Label>
          <Select value={fields.priority || "__use_template"} onValueChange={(v) => onUpdate({ priority: v === "__use_template" ? null : v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>
              <SelectItem value="__use_template">Use template default</SelectItem>
              {["Low","Medium","High","Critical"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="text-[11px] text-white/50 italic">
        On save, the template's mission, goals, scope, instructions, and deliverables carry into the new project. Tasks and milestones are generated automatically.
      </div>
    </div>
  );
}

// ---- Task ----
function TaskFields({ fields, onUpdate, idx }) {
  return (
    <>
      <Input value={fields.title || ""} onChange={(e) => onUpdate({ title: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Task title" data-testid={`draft-title-${idx}`}/>
      <Textarea rows={4} value={fields.description || ""} onChange={(e) => onUpdate({ description: e.target.value })}
        placeholder="Description (optional)" className="bg-white/5 border-white/10 text-sm mb-3"/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Priority</Label>
          <Select value={fields.priority || "Normal"} onValueChange={(v) => onUpdate({ priority: v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["Low","Normal","Medium","High","Critical"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className={R.label}>Status</Label>
          <Select value={fields.status || "Not started"} onValueChange={(v) => onUpdate({ status: v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["Not started","In Progress","Blocked","Done"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className={R.label}>Due date</Label>
          <Input type="date" value={fields.due_date || ""} onChange={(e) => onUpdate({ due_date: e.target.value || null })} className={R.input}/></div>
        <div><Label className={R.label}>Tags</Label>
          <Input value={(fields.tags || []).join(", ")} onChange={(e) => onUpdate({ tags: e.target.value.split(",").map(x => x.trim().toLowerCase()).filter(Boolean) })} className={R.input} placeholder="e.g. finance, urgent"/></div>
        <div className="sm:col-span-2">
          <Label className={R.label}>Assignee</Label>
          <div className="mt-1">
            {fields.assignee_new_name && !fields.assignee_contact_id ? (
              <div className="p-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] space-y-2">
                <div className="text-xs text-amber-200">New contact suggested — save will create it</div>
                <Input value={fields.assignee_new_name} onChange={(e) => onUpdate({ assignee_new_name: e.target.value })} placeholder="Name" className="bg-white/5 border-white/10 h-9 text-sm"/>
                <Input value={fields.assignee_new_email || ""} onChange={(e) => onUpdate({ assignee_new_email: e.target.value })} placeholder="Email (optional)" className="bg-white/5 border-white/10 h-9 text-sm"/>
                <div className="flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => onUpdate({ assignee_new_name: null, assignee_new_email: null })} className="text-white/60 hover:text-white hover:bg-white/10">Skip</Button>
                </div>
              </div>
            ) : (
              <ContactPicker
                value={fields.assignee_contact_id}
                onChange={(id) => onUpdate({ assignee_contact_id: id })}
                entityId={fields.entity_id}
                projectId={fields.project_id || undefined}
              />
            )}
          </div>
        </div>
        {/* Optional outreach/research context. Email/phone from the prompt
            are proposed as a NEW Contact so they live in the canonical
            Contacts DB — never duplicated onto the Task itself. */}
        <div className="sm:col-span-2 pt-2 border-t border-white/5">
          <Label className={R.label}>Reference URL <span className="text-white/30 normal-case">(optional)</span></Label>
          <Input
            value={fields.reference_url || ""}
            onChange={(e) => onUpdate({ reference_url: e.target.value })}
            placeholder="https://…"
            className={R.input}
            data-testid={`draft-reference-url-${idx}`}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className={R.label}>Related contact <span className="text-white/30 normal-case">(optional)</span></Label>
          <div className="mt-1" data-testid={`draft-related-contact-${idx}`}>
            {(fields.related_new_name || fields.related_new_email || fields.related_new_phone) && !fields.related_contact_id ? (
              <div className="p-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] space-y-2">
                <div className="text-xs text-amber-200">New contact suggested — save will create it (email/phone stored on Contact, not Task)</div>
                <Input value={fields.related_new_name || ""} onChange={(e) => onUpdate({ related_new_name: e.target.value })} placeholder="Name" className="bg-white/5 border-white/10 h-9 text-sm" data-testid={`draft-related-new-name-${idx}`}/>
                <Input value={fields.related_new_email || ""} onChange={(e) => onUpdate({ related_new_email: e.target.value })} placeholder="Email (optional)" className="bg-white/5 border-white/10 h-9 text-sm" data-testid={`draft-related-new-email-${idx}`}/>
                <Input value={fields.related_new_phone || ""} onChange={(e) => onUpdate({ related_new_phone: e.target.value })} placeholder="Phone (optional)" className="bg-white/5 border-white/10 h-9 text-sm" data-testid={`draft-related-new-phone-${idx}`}/>
                <div className="flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => onUpdate({ related_new_name: null, related_new_email: null, related_new_phone: null })} className="text-white/60 hover:text-white hover:bg-white/10">Skip</Button>
                </div>
              </div>
            ) : (
              <ContactPicker
                value={fields.related_contact_id}
                onChange={(id) => onUpdate({ related_contact_id: id })}
                entityId={fields.entity_id}
                projectId={fields.project_id || undefined}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ---- Calendar ----
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function localInputToIso(v) { return v ? new Date(v).toISOString() : null; }

function CalendarFields({ fields, onUpdate, idx }) {
  return (
    <>
      <Input value={fields.title || ""} onChange={(e) => onUpdate({ title: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Event title" data-testid={`draft-title-${idx}`}/>
      <Textarea rows={4} value={fields.description || ""} onChange={(e) => onUpdate({ description: e.target.value })}
        placeholder="Description" className="bg-white/5 border-white/10 text-sm mb-3"/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Type</Label>
          <Select value={fields.event_type || "meeting"} onValueChange={(v) => onUpdate({ event_type: v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["meeting","deadline","launch","production","payment","followup","milestone"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className={R.label}>Location</Label>
          <Input value={fields.location || ""} onChange={(e) => onUpdate({ location: e.target.value })} className={R.input}/></div>
        <div><Label className={R.label}>Starts at</Label>
          <Input type="datetime-local" value={isoToLocalInput(fields.starts_at)} onChange={(e) => onUpdate({ starts_at: localInputToIso(e.target.value) })} className={R.input}/></div>
        <div><Label className={R.label}>Ends at</Label>
          <Input type="datetime-local" value={isoToLocalInput(fields.ends_at)} onChange={(e) => onUpdate({ ends_at: localInputToIso(e.target.value) })} className={R.input}/></div>
        <div className="sm:col-span-2">
          <Label className={R.label}>Primary contact (optional)</Label>
          <div className="mt-1">
            <ContactPicker value={fields.contact_id} onChange={(id) => onUpdate({ contact_id: id })} entityId={fields.entity_id} projectId={fields.project_id || undefined}/>
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label className={R.label}>Participants / attendees</Label>
          <div className="mt-1" data-testid={`draft-participants-${idx}`}>
            <MultiContactPicker
              values={fields.participant_contact_ids || []}
              onChange={(ids) => onUpdate({ participant_contact_ids: ids })}
              entityId={fields.entity_id}
              projectId={fields.project_id || undefined}
            />
            {(fields.new_participants || []).length > 0 && (
              <div className="mt-2 p-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] space-y-2">
                <div className="text-xs text-amber-200">New contacts — save will create these:</div>
                <div className="flex flex-wrap gap-1.5">
                  {(fields.new_participants || []).map((np, i) => (
                    <div key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-400/30 text-xs" data-testid={`new-participant-${i}`}>
                      <span>{np.name}{np.email ? ` · ${np.email}` : ""}</span>
                      <button type="button"
                        onClick={() => onUpdate({ new_participants: (fields.new_participants || []).filter((_, j) => j !== i) })}
                        className="hover:bg-white/10 rounded-full p-0.5" aria-label={`Remove ${np.name}`}>
                        <X className="w-3 h-3"/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ---- Expenditure ----
function ExpenditureFields({ fields, onUpdate, idx }) {
  const isRevenue = (fields.kind || "expense") === "revenue";
  return (
    <>
      <Input value={fields.description || ""} onChange={(e) => onUpdate({ description: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Description" data-testid={`draft-title-${idx}`}/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Kind</Label>
          <Select value={fields.kind || "expense"} onValueChange={(v) => onUpdate({ kind: v, status: v === "revenue" ? (fields.status || "Received") : null })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["expense","revenue","expected"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {isRevenue && (
          <div>
            <Label className={R.label}>Status</Label>
            <Select value={fields.status || "Received"} onValueChange={(v) => onUpdate({ status: v })}>
              <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
              <SelectContent className={R.content}>{["Received","Outstanding"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className={R.label}>Amount</Label>
          <div className="mt-1 flex items-stretch gap-2">
            <div className="flex-1 min-w-0">
              <CurrencyInput value={fields.amount} currency={fields.currency || "USD"} onChange={(v) => onUpdate({ amount: Number(v) || 0 })} className="bg-white/5 border-white/10 h-9 text-sm"/>
            </div>
            <div className="w-24 flex-shrink-0">
              <CurrencySelect value={fields.currency || "USD"} onChange={(v) => onUpdate({ currency: v })}/>
            </div>
          </div>
        </div>
        <div><Label className={R.label}>Date</Label>
          <Input type="date" value={fields.date || ""} onChange={(e) => onUpdate({ date: e.target.value })} className={R.input}/></div>
        <div><Label className={R.label}>Category</Label>
          <Input value={fields.category || ""} onChange={(e) => onUpdate({ category: e.target.value })} className={R.input}/></div>
        {isRevenue ? (
          <div className="sm:col-span-2"><Label className={R.label}>Source / Client</Label>
            <Input value={fields.source_client || ""} onChange={(e) => onUpdate({ source_client: e.target.value })} className={R.input}/></div>
        ) : (
          <div className="sm:col-span-2"><Label className={R.label}>Vendor / payee</Label>
            <Input value={fields.vendor || ""} onChange={(e) => onUpdate({ vendor: e.target.value })} className={R.input}/></div>
        )}
      </div>
    </>
  );
}

// ---- Note ----
function NoteFields({ fields, onUpdate, idx }) {
  return (
    <>
      <Input value={fields.title || ""} onChange={(e) => onUpdate({ title: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Note title" data-testid={`draft-title-${idx}`}/>
      <Textarea rows={4} value={fields.content || ""} onChange={(e) => onUpdate({ content: e.target.value })}
        placeholder="Content" className="bg-white/5 border-white/10 text-sm mb-3"/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className={R.label}>Type</Label>
          <Select value={fields.entry_type || "note"} onValueChange={(v) => onUpdate({ entry_type: v })}>
            <SelectTrigger className={R.trigger}><SelectValue/></SelectTrigger>
            <SelectContent className={R.content}>{["note","idea","meeting_notes","decision","direction_change"].map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className={R.label}>Date</Label>
          <Input type="date" value={fields.date || ""} onChange={(e) => onUpdate({ date: e.target.value })} className={R.input}/></div>
        {fields.entry_type === "decision" && (
          <div className="sm:col-span-2"><Label className={R.label}>Decision reason</Label>
            <Textarea rows={3} value={fields.decision_reason || ""} onChange={(e) => onUpdate({ decision_reason: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-sm"/></div>
        )}
      </div>
    </>
  );
}

// ---- Contact ----
function ContactFields({ entities, fields, onUpdate, idx }) {
  return (
    <>
      <Input value={fields.full_name || ""} onChange={(e) => onUpdate({ full_name: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Full name" data-testid={`draft-title-${idx}`}/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className={R.label}>Email</Label>
          <Input type="email" value={fields.email || ""} onChange={(e) => onUpdate({ email: e.target.value })} className={R.input}/></div>
        <div><Label className={R.label}>Phone</Label>
          <Input value={fields.phone || ""} onChange={(e) => onUpdate({ phone: e.target.value })} className={R.input}/></div>
        <div><Label className={R.label}>Company</Label>
          <Input value={fields.company || ""} onChange={(e) => onUpdate({ company: e.target.value })} className={R.input}/></div>
        <div><Label className={R.label}>Role</Label>
          <Input value={fields.role || ""} onChange={(e) => onUpdate({ role: e.target.value })} className={R.input}/></div>
        <div className="sm:col-span-2">
          <Label className={R.label}>Link to entity (optional)</Label>
          <Select value={fields.entity_id || "__none"} onValueChange={(v) => onUpdate({ entity_id: v === "__none" ? null : v })}>
            <SelectTrigger className={R.trigger}><SelectValue placeholder="No entity"/></SelectTrigger>
            <SelectContent className={R.content}>
              <SelectItem value="__none">No entity</SelectItem>
              {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2"><Label className={R.label}>Notes</Label>
          <Textarea rows={3} value={fields.notes || ""} onChange={(e) => onUpdate({ notes: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-sm"/></div>
      </div>
    </>
  );
}

// ---- Email ----
function EmailFields({ fields, onUpdate, idx }) {
  return (
    <>
      <Input value={fields.subject || ""} onChange={(e) => onUpdate({ subject: e.target.value })}
        className="bg-transparent border-0 border-b border-white/10 focus-visible:ring-0 focus-visible:border-white/40 rounded-none px-0 text-base font-medium mb-3"
        placeholder="Subject" data-testid={`draft-title-${idx}`}/>
      <Textarea rows={6} value={fields.body || ""} onChange={(e) => onUpdate({ body: e.target.value })}
        placeholder="Body (HTML supported)" className="bg-white/5 border-white/10 text-sm mb-3 font-mono"/>
      <div>
        <Label className={R.label}>Send to</Label>
        <div className="mt-1">
          <ContactPicker value={fields.contact_id} onChange={(id) => onUpdate({ contact_id: id })} entityId={fields.entity_id} projectId={fields.project_id || undefined}/>
        </div>
        {fields.contact_id && fields.contact_email && (
          <div className="text-[11px] text-white/40 mt-1">To: {fields.contact_email}</div>
        )}
      </div>
    </>
  );
}
